import React from 'react';
import { 
  Play, 
  ArrowRight, 
  Users, 
  Headphones, 
  Globe, 
  Smile, 
  ShieldCheck, 
  HeartHandshake,
  Target,
  Eye,
  Gem
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function AboutPage() {
  const stats = [
    {
      icon: Users,
      value: "10,000+",
      label: "Entreprises accompagnées dans le monde",
      colorClass: "bg-blue-50 text-blue-600"
    },
    {
      icon: Headphones,
      value: "5M+",
      label: "Appels traités chaque mois",
      colorClass: "bg-emerald-50 text-emerald-600"
    },
    {
      icon: Globe,
      value: "20+",
      label: "Pays couverts",
      colorClass: "bg-amber-50 text-tertiary"
    },
    {
      icon: Smile,
      value: "98%",
      label: "Taux de satisfaction client",
      colorClass: "bg-purple-50 text-purple-600"
    },
    {
      icon: ShieldCheck,
      value: "99,9%",
      label: "Uptime de la plateforme",
      colorClass: "bg-sky-50 text-sky-600"
    },
    {
      icon: HeartHandshake,
      value: "24/7",
      label: "Support et accompagnement dédiés",
      colorClass: "bg-rose-50 text-rose-500"
    }
  ];

  return (
    <PublicPageLayout>
        
        {/* --- HERO SECTION --- */}
        <section className="px-6 lg:px-12 py-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Gauche : Textes */}
            <div className="lg:col-span-5 space-y-4">
              <span className="text-xs font-extrabold text-blue-600 uppercase tracking-widest">
                À propos de nous
              </span>

              <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-[1.15]">
                Nous connectons les entreprises à leurs clients, à <span className="text-blue-600">la vitesse de la lumière</span><span className="text-tertiary">.</span>
              </h1>

              <p className="text-slate-600 text-sm lg:text-base leading-relaxed max-w-lg">
                IZI Call Center est une plateforme cloud nouvelle génération conçue pour aider les entreprises à offrir une expérience client exceptionnelle sur tous les canaux de communication.
              </p>

              <div className="flex items-center gap-6 pt-1">
                <button className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2 transition-all">
                  <div className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center">
                    <Play size={8} fill="currentColor" className="ml-0.5" />
                  </div>
                  Regarder la vidéo
                </button>

                <a href="#histoire" className="text-sm font-bold text-blue-600 hover:underline flex items-center gap-1">
                  Découvrir notre histoire <ArrowRight size={15} />
                </a>
              </div>
            </div>

            {/* Droite : Photo */}
            <div className="lg:col-span-7 relative">
              <div className="rounded-2xl overflow-hidden shadow-sm relative w-full h-[260px] lg:h-[320px]">
                <img 
                  src="/about/hero.png" 
                  alt="Équipe IZI Call Center" 
                  className="w-full h-full object-cover block"
                />

                {/* Citation flottante - style maquette */}
                <div className="absolute bottom-4 left-4 max-w-[220px] bg-white/20 backdrop-blur-md text-white p-4 rounded-xl border border-white/20 shadow-lg">
                  <span className="text-tertiary text-4xl font-serif leading-none block mb-1">“</span>
                  <p className="text-xs leading-relaxed text-white/90">
                    Notre mission est simple : aider chaque entreprise à bâtir des relations durables avec ses clients grâce à la technologie et à l'humain.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* --- STATISTIQUES --- */}
        <section className="px-6 lg:px-12 max-w-7xl mx-auto my-8">
          <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm py-5 px-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${stat.colorClass}`}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <div className="text-lg lg:text-xl font-extrabold text-blue-600 leading-none mb-0.5">
                      {stat.value}
                    </div>
                    <div className="text-[11px] text-slate-500 font-medium leading-tight">
                      {stat.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* --- ÉQUIPE & PILIERS --- */}
        <section className="px-6 lg:px-12 max-w-7xl mx-auto mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Carte Équipe */}
            <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-5 bg-blue-600 text-white p-6 flex flex-col justify-between">
                <div>
                  <h3 className="text-sm font-bold leading-snug mb-2">
                    Une équipe passionnée au service de votre <span className="text-amber-300">croissance</span>.
                  </h3>
                  <p className="text-xs text-blue-100 leading-relaxed mb-3">
                    Experts en technologie, relation client et innovation, nous travaillons chaque jour pour vous offrir le meilleur.
                  </p>
                </div>
                <a href="#equipe" className="text-xs font-bold text-white hover:underline flex items-center gap-1">
                  Rencontrer l'équipe <ArrowRight size={13} />
                </a>
              </div>
              <div className="md:col-span-7 relative min-h-[180px]">
                <img 
                  src="/about/team-group.jpg" 
                  alt="Équipe IZI" 
                  className="w-full h-full object-cover absolute inset-0"
                />
              </div>
            </div>

            {/* Mission, Vision, Valeurs */}
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                  <Target size={18} />
                </div>
                <h4 className="font-bold text-slate-900 text-sm mb-1.5">Notre mission</h4>
                <p className="text-slate-500 text-xs leading-relaxed">
                  Fournir aux entreprises des outils puissants et simples pour gérer leurs communications et offrir une expérience client exceptionnelle.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-amber-50 text-tertiary flex items-center justify-center mb-3">
                  <Eye size={18} />
                </div>
                <h4 className="font-bold text-slate-900 text-sm mb-1.5">Notre vision</h4>
                <p className="text-slate-500 text-xs leading-relaxed">
                  Devenir la plateforme de référence en Afrique et dans le monde pour la gestion de la relation client omnicanale.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                  <Gem size={18} />
                </div>
                <h4 className="font-bold text-slate-900 text-sm mb-1.5">Nos valeurs</h4>
                <ul className="space-y-0.5 text-xs text-slate-600 list-disc list-inside">
                  <li>Orientation client</li>
                  <li>Innovation continue</li>
                  <li>Intégrité et transparence</li>
                  <li>Excellence opérationnelle</li>
                  <li>Engagement et passion</li>
                </ul>
              </div>

            </div>

          </div>
        </section>

        {/* --- PARTENAIRES --- */}
        <section className="py-4 bg-white border-t border-slate-100 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
            <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">
              Ils nous font confiance
            </span>
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-5 text-sm font-bold text-slate-700">
              <span className="flex items-center gap-1"><span className="text-blue-600">Z</span> IZI LIFE PAY</span>
              <span className="flex items-center gap-1"><span className="text-blue-600">Z</span> IZI TRUST CAPITAL</span>
              <span className="flex items-center gap-1"><span className="text-blue-600">Z</span> IZI FLOW</span>
              <span className="text-amber-600">AfrikMarket</span>
              <span className="text-slate-800">EcoBank</span>
              <span className="text-red-600">UBA</span>
              <span className="text-yellow-500">MTN</span>
              <span className="text-slate-400 font-normal italic">et bien d'autres...</span>
            </div>
          </div>
        </section>

    </PublicPageLayout>
  );
}